/**
 * Data client for /api/sessions, /api/decisions, /api/ideas, /api/tasks,
 * /api/memories — Phase 1C-05.
 *
 * Reads that don't need mutation land through supabase-js (PostgREST +
 * RLS). Writes and non-CRUD actions (supersede, promote, summarize)
 * go through our backend which uses the postgres role.
 */
import { API } from "@/lib/api";
import { getAccessToken } from "@/lib/supabaseClient";

async function authFetch(path, init = {}) {
    const tok = await getAccessToken();
    const headers = {
        "Content-Type": "application/json",
        ...(init.headers || {}),
        ...(tok ? { Authorization: `Bearer ${tok}` } : {}),
    };
    const r = await fetch(`${API}${path}`, { ...init, headers });
    if (!r.ok) {
        let msg = `${init.method || "GET"} ${path} → ${r.status}`;
        try {
            const j = await r.json();
            msg += `: ${j.detail || JSON.stringify(j)}`;
        } catch {
            /* ignore */
        }
        throw new Error(msg);
    }
    if (r.status === 204) return null;
    return r.json();
}

// --- sessions -------------------------------------------------------------

export const createSession = (body) =>
    authFetch("/sessions", { method: "POST", body: JSON.stringify(body || {}) });

export const patchSession = (id, body) =>
    authFetch(`/sessions/${id}`, { method: "PATCH", body: JSON.stringify(body) });

export const getSession = (id) => authFetch(`/sessions/${id}`);

export const appendTurn = (id, body) =>
    authFetch(`/sessions/${id}/turns`, {
        method: "POST",
        body: JSON.stringify(body),
    });

export const enqueueSummarize = (id) =>
    authFetch(`/sessions/${id}/summarize`, { method: "POST" });

export const getSummary = async (id) => {
    // 404 is a normal state ("no summary yet") — surface null instead of throwing.
    const tok = await getAccessToken();
    const r = await fetch(`${API}/sessions/${id}/summary`, {
        headers: tok ? { Authorization: `Bearer ${tok}` } : {},
    });
    if (r.status === 404) return null;
    if (!r.ok) throw new Error(`getSummary(${id}) → ${r.status}`);
    return r.json();
};

export const promoteFromSummary = (id, body) =>
    authFetch(`/sessions/${id}/promote`, {
        method: "POST",
        body: JSON.stringify(body),
    });

// --- decisions / ideas / tasks / memories ---------------------------------

export const createDecision = (body) =>
    authFetch("/decisions", { method: "POST", body: JSON.stringify(body) });

export const supersedeDecision = (id, body) =>
    authFetch(`/decisions/${id}/supersede`, {
        method: "POST",
        body: JSON.stringify(body),
    });

export const patchDecision = (id, body) =>
    authFetch(`/decisions/${id}`, { method: "PATCH", body: JSON.stringify(body) });

export const patchIdea = (id, body) =>
    authFetch(`/ideas/${id}`, { method: "PATCH", body: JSON.stringify(body) });

export const patchTask = (id, body) =>
    authFetch(`/tasks/${id}`, { method: "PATCH", body: JSON.stringify(body) });

export const patchMemory = (id, body) =>
    authFetch(`/memories/${id}`, { method: "PATCH", body: JSON.stringify(body) });
